import type { PaletteMode } from '@mui/material'
import { CINZAS, MARCA, MODO_CLARO, MODO_ESCURO, SEMANTICAS } from '@/contratos'

/**
 * Paleta do painel — mesma estrutura do arsal-drive (`@core/theme/palette.ts`),
 * mas SEM hex literal: todo valor vem de @/contratos/theme, que e a fonte
 * unica dos tokens da agencia (primario institucional #02559b) e e consumida
 * tambem pelo app de campo.
 */

/**
 * DIVERGENCIA DELIBERADA do arsal-drive: la o triplet mora em
 * `customColors.main` / `.light` / `.dark`. Aqui ele e `mainRgb` / `lightRgb` /
 * `darkRgb`.
 *
 * Motivo: o MUI monta as variantes do Alert varrendo TODA chave da paleta que
 * tenha `.main` e `.light`, e roda `darken()` nelas. Como o triplet ("50, 71,
 * 92") nao e uma cor CSS valida, o `customColors` do Drive derruba qualquer tela
 * que use <Alert> ("MUI: Unsupported `50, 71, 92` color"). O Drive nao esbarra
 * nisso porque nao usa Alert; este painel usa em quase todas as telas.
 */
declare module '@mui/material/styles' {
  interface Palette {
    customColors: {
      mainRgb: string
      lightRgb: string
      darkRgb: string
      bodyBg: string
      trackBg: string
      avatarBg: string
      tableHeaderBg: string
      darkPaperBg: string
      lightPaperBg: string
      collapseTogglerBg: string
    }
  }
  interface PaletteOptions {
    customColors?: Partial<Palette['customColors']>
  }
}

/**
 * O design system deriva bordas, inputs e chips de `rgba(<triplet>, alpha)`. O
 * shared expoe a cor ja com alpha (`rgba(50, 71, 92, 0.87)`), entao extraimos o
 * triplet dela em vez de repetir o literal — assim existe UM lugar com a cor.
 */
export function tripletDe(cor: string): string {
  const m = cor.match(/rgba?\(([^)]+?),\s*[\d.]+\)$/)
  return m?.[1]?.trim() ?? '0, 0, 0'
}

export const TRIPLET_CLARO = tripletDe(MODO_CLARO.texto)
export const TRIPLET_ESCURO = tripletDe(MODO_ESCURO.texto)

const palette = (mode: PaletteMode) => {
  const claro = mode === 'light'
  const superficie = claro ? MODO_CLARO : MODO_ESCURO
  const mainColor = claro ? TRIPLET_CLARO : TRIPLET_ESCURO

  return {
    customColors: {
      darkRgb: TRIPLET_ESCURO,
      mainRgb: mainColor,
      lightRgb: TRIPLET_CLARO,
      lightPaperBg: MODO_CLARO.superficie,
      darkPaperBg: MODO_ESCURO.superficie,
      // Igual ao bodyBg (sem skin bordered neste painel), como no arsal-join.
      collapseTogglerBg: superficie.fundo,
      bodyBg: superficie.fundo,
      trackBg: superficie.fundoTrilha,
      avatarBg: superficie.fundoAvatar,
      tableHeaderBg: superficie.fundoCabecalhoTabela,
    },
    mode,
    common: { black: '#000', white: '#FFF' },
    primary: MARCA.primaria,
    secondary: MARCA.secundaria,
    error: SEMANTICAS.erro,
    warning: SEMANTICAS.alerta,
    info: SEMANTICAS.info,
    success: SEMANTICAS.sucesso,
    grey: {
      ...CINZAS,
      A100: CINZAS[100],
      A200: CINZAS[200],
      A400: CINZAS[400],
      A700: CINZAS[700],
    },
    text: {
      primary: superficie.texto,
      secondary: superficie.textoSecundario,
      disabled: superficie.textoDesabilitado,
    },
    divider: superficie.divisor,
    background: {
      paper: superficie.superficie,
      default: superficie.fundo,
    },
    action: {
      active: `rgba(${mainColor}, 0.54)`,
      hover: `rgba(${mainColor}, 0.04)`,
      selected: `rgba(${mainColor}, 0.08)`,
      disabled: `rgba(${mainColor}, 0.26)`,
      disabledBackground: `rgba(${mainColor}, 0.12)`,
      focus: `rgba(${mainColor}, 0.12)`,
    },
  }
}

export default palette
